import { useQueryClient, UseMutationOptions } from 'react-query';
import produce from 'immer';

import {
  useGetLeaderboardConfigsQuery,
  CreateUpdateLeaderboardConfigMutation,
  CreateUpdateLeaderboardConfigMutationVariables,
  GetLeaderboardConfigsQuery,
  LeaderboardConfigFragment,
  GenericStatus,
} from 'api';
import { useNotify } from 'hooks';

type Mutation = CreateUpdateLeaderboardConfigMutation;
type MutationVariables = CreateUpdateLeaderboardConfigMutationVariables;
type QueryData = GetLeaderboardConfigsQuery;

const ID_STUB = '-1';

const useCreateLeaderboard = () => {
  const queryClient = useQueryClient();
  const queryKey = useGetLeaderboardConfigsQuery.getKey();
  const notify = useNotify();

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    onMutate: async mutationArgs => {
      await queryClient.cancelQueries(queryKey);

      const newLeaderboard: LeaderboardConfigFragment = {
        id: ID_STUB,
        created_at: 0,
        all_users: false,
        description: null,
        segment_ids: [],
        segments: [],
        start_at: null,
        end_at: null,
        leaderboard_status: GenericStatus.Disabled,
        title: '',
        ...mutationArgs.data,
      };

      const baseState = queryClient.getQueryData<QueryData>(queryKey);
      const nextState =
        baseState &&
        produce(baseState, draftState => {
          draftState.getLeaderboardConfigs.unshift(newLeaderboard);
        });

      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, nextState);
      }

      return baseState;
    },
    onError: (error, mutationArgs, baseState: QueryData) => {
      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, baseState);
      }
      notify.mutationError();
    },
    onSettled: ({ createUpdateLeaderboardConfig }) => {
      const baseState = queryClient.getQueryData<QueryData>(queryKey);
      const nextState =
        baseState &&
        produce(baseState, draftState => {
          const index = draftState.getLeaderboardConfigs.findIndex(config => config.id === ID_STUB);
          draftState.getLeaderboardConfigs[index] = createUpdateLeaderboardConfig;
        });

      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, nextState);
      }
    },
  };

  return mutationOptions;
};

export default useCreateLeaderboard;
